<template><markdown-wrapper><h1 id="tooltip">Tooltip <a class="header-anchor" href="#tooltip">&#128279;</a></h1>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>The first element appear in <code v-pre>&lt;tooltip&gt;</code> node will be the trigger element. You can also use <code v-pre>target</code> to reference it from outside the component.</p>
<p><strong>Note</strong>: Tooltips with zero-length texts are never displayed.</p>
<p>Hover over the button below to toggle tooltips:</p>
<tooltip-example ref="tooltip-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;btn&quot;</span>&gt;</span>Hover me!<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">tooltip</span> <span class="hljs-attr">text</span>=<span class="hljs-string">&quot;Static tooltip content goes here&quot;</span> <span class="hljs-attr">target</span>=<span class="hljs-string">&quot;#btn&quot;</span>/&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-example.vue --&gt;</span>
</code></pre>
<h3 id="trigger-target">Trigger target <a class="header-anchor" href="#trigger-target">&#128279;</a></h3>
<p>Order to decide the tooltip trigger:</p>
<ol>
<li>Use <code v-pre>target</code> if exist.</li>
<li>Use element in default slot with <code v-pre>data-role=&quot;trigger&quot;</code> attribute if exist.</li>
<li>Use the first element present in default slot.</li>
</ol>
<p>A <code v-pre>target</code> can be:</p>
<ul>
<li>Selector that can be recognized by <code v-pre>querySelect</code>.</li>
<li>Reference to Element.</li>
<li>Reference to Component.</li>
</ul>
<h2 id="directive">Directive <a class="header-anchor" href="#directive">&#128279;</a></h2>
<p>You can also simply use tooltips via <code v-pre>v-tooltip</code> directive:</p>
<tooltip-directive ref="tooltip-directive"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content goes here&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Hover me!<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-directive.vue --&gt;</span>
</code></pre>
<h2 id="placements">Placements <a class="header-anchor" href="#placements">&#128279;</a></h2>
<p>Supported placements:</p>
<ul>
<li><strong>top</strong> (Default)</li>
<li><strong>right</strong></li>
<li><strong>bottom</strong></li>
<li><strong>left</strong></li>
</ul>
<tooltip-placements ref="tooltip-placements"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.left</span>=<span class="hljs-string">&quot;&#x27;Tooltip content on left&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Left<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.top</span>=<span class="hljs-string">&quot;&#x27;Tooltip content on top&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Top<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.bottom</span>=<span class="hljs-string">&quot;&#x27;Tooltip content on bottom&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Bottom<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.right</span>=<span class="hljs-string">&quot;&#x27;Tooltip content on right&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Right<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-placements.vue --&gt;</span>
</code></pre>
<h2 id="auto-placement">Auto placement <a class="header-anchor" href="#auto-placement">&#128279;</a></h2>
<p>Tooltips will try to find the best placement for displaying while <code v-pre>auto-placement</code> is set to <code v-pre>true</code> (by default) base on the default placement setting. Useful while there does not have enough space to show the entire tooltip content.</p>
<p><code v-pre>auto-placement</code> try order: right -&gt; bottom -&gt; left -&gt; top, and use the set one if none of these matched.</p>
<h2 id="viewport">Viewport <a class="header-anchor" href="#viewport">&#128279;</a></h2>
<p>Keeps the tooltip within the bounds of this element.</p>
<tooltip-viewport ref="tooltip-viewport"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;tooltip-viewport&quot;</span> <span class="hljs-attr">style</span>=<span class="hljs-string">&quot;display: inline-block; padding: 20px; background-color: #eee&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content auto&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Auto<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.left</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content on left&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Left<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.top</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content on top&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Top<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.bottom</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content on bottom&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Bottom<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.right</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content on right&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Right<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip</span>=<span class="hljs-string">&quot;{text:&#x27;Tooltip content auto&#x27;, viewport:&#x27;#tooltip-viewport&#x27;}&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Auto<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-viewport.vue --&gt;</span>
</code></pre>
<h2 id="triggers">Triggers <a class="header-anchor" href="#triggers">&#128279;</a></h2>
<p>Supported triggers:</p>
<ul>
<li><code v-pre>hover</code> show on mouseenter, hide on mouseleave.</li>
<li><code v-pre>focus</code> show on focus, hide on blur.</li>
<li><code v-pre>hover-focus</code> (Default) combination of hover and focus trigger.</li>
<li><code v-pre>click</code> toggle on trigger click.</li>
<li><code v-pre>outside-click</code> same as click, but not close on tooltip click and close on outside click.</li>
</ul>
<tooltip-triggers ref="tooltip-triggers"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Hover-Focus (Default)<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.hover</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Hover<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.focus</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Focus<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.click</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Click<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">v-tooltip.outside-click</span>=<span class="hljs-string">&quot;&#x27;Static tooltip content&#x27;&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Outside-Click<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-triggers.vue --&gt;</span>
</code></pre>
<h2 id="manual-trigger">Manual trigger <a class="header-anchor" href="#manual-trigger">&#128279;</a></h2>
<p>Set <code v-pre>trigger</code> prop to <code v-pre>manual</code> to disable all the event listeners, and controls tooltips show / hide only by <code v-pre>v-model</code> change.</p>
<tooltip-manual-trigger ref="tooltip-manual-trigger"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">tooltip</span> <span class="hljs-attr">text</span>=<span class="hljs-string">&quot;Static tooltip content goes here&quot;</span> <span class="hljs-attr">trigger</span>=<span class="hljs-string">&quot;manual&quot;</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;show&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">btn</span>&gt;</span>You Can&#x27;t Trigger Tooltip Here...<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;/<span class="hljs-name">tooltip</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">hr</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;show = !show&quot;</span>&gt;</span>Toggle Tooltip<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">show</span>: <span class="hljs-literal">false</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-manual-trigger.vue --&gt;</span>
</code></pre>
<h2 id="disable-tooltip">Disable tooltip <a class="header-anchor" href="#disable-tooltip">&#128279;</a></h2>
<p>Set <code v-pre>enable</code> prop to <code v-pre>false</code> to disable a tooltip.</p>
<tooltip-disable ref="tooltip-disable"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">tooltip</span> <span class="hljs-attr">text</span>=<span class="hljs-string">&quot;Static tooltip content goes here&quot;</span> <span class="hljs-attr">:enable</span>=<span class="hljs-string">&quot;false&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span>&gt;</span>Disabled Tooltip<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">tooltip</span>&gt;</span>
<span class="hljs-comment">&lt;!-- tooltip-disable.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="tooltip-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/tooltip/Tooltip.vue">Tooltip</a> <a class="header-anchor" href="#tooltip-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td>Boolean</td>
<td></td>
<td></td>
<td>Show / hide the tooltip.</td>
</tr>
<tr>
<td><code v-pre>target</code></td>
<td></td>
<td></td>
<td></td>
<td>Tooltip trigger, can be a select or reference to Element / Component.</td>
</tr>
<tr>
<td><code v-pre>tag</code></td>
<td>String</td>
<td>span</td>
<td></td>
<td>The HTML tag that render the component.</td>
</tr>
<tr>
<td><code v-pre>text</code></td>
<td>String</td>
<td></td>
<td></td>
<td>The tooltip text.</td>
</tr>
<tr>
<td><code v-pre>enable</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Enable the tooltip.</td>
</tr>
<tr>
<td><code v-pre>enterable</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Whether mouse can enter the tooltip.</td>
</tr>
<tr>
<td><code v-pre>placement</code></td>
<td>String</td>
<td>top</td>
<td></td>
<td>The tooltip placement, support <code v-pre>top</code> / <code v-pre>bottom</code> / <code v-pre>left</code> / <code v-pre>right</code>.</td>
</tr>
<tr>
<td><code v-pre>auto-placement</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Try to auto adjust the placement if the set one does not have enough space to show.</td>
</tr>
<tr>
<td><code v-pre>trigger</code></td>
<td>String</td>
<td>hover-focus</td>
<td></td>
<td>The tooltip trigger event, support <code v-pre>hover</code> / <code v-pre>focus</code> / <code v-pre>hover-focus</code> / <code v-pre>click</code> / <code v-pre>outside-click</code> / <code v-pre>manual</code></td>
</tr>
<tr>
<td><code v-pre>append-to</code></td>
<td>String</td>
<td>body</td>
<td></td>
<td>Element selector that the tooltip append to.</td>
</tr>
<tr>
<td><code v-pre>transition-duration</code></td>
<td>Number</td>
<td>150</td>
<td></td>
<td>The tooltip show / hide transition time in ms.</td>
</tr>
<tr>
<td><code v-pre>show-delay</code></td>
<td>Number</td>
<td>0</td>
<td></td>
<td>(0.30.0+) Delay showing the tooltip (ms).</td>
</tr>
<tr>
<td><code v-pre>hide-delay</code></td>
<td>Number</td>
<td>0</td>
<td></td>
<td>(0.30.0+) Delay hidding the tooltip (ms).</td>
</tr>
<tr>
<td><code v-pre>viewport</code></td>
<td>String or Function</td>
<td></td>
<td></td>
<td>(0.31.0+) Keeps the tooltip within the bounds of this element. Example: viewport: '#viewport'. If a function is given, it is called with the triggering element DOM node as its only argument.</td>
</tr>
<tr>
<td><code v-pre>custom-class</code></td>
<td>String</td>
<td></td>
<td></td>
<td>(0.33.0+) Apply a custom css class to the tooltip.</td>
</tr>
</tbody>
</table></div><h3 id="events">Events <a class="header-anchor" href="#events">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Params</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>show</code></td>
<td></td>
<td>Fire after tooltip show.</td>
</tr>
<tr>
<td><code v-pre>hide</code></td>
<td></td>
<td>Fire after tooltip hide.</td>
</tr>
</tbody>
</table></div><h3 id="directive-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/directives/tooltip/tooltip.js">Directive</a> <a class="header-anchor" href="#directive-1">&#128279;</a></h3>
<p>The binding value will be the text content of corresponding tooltip.</p>
<p><strong>Simplest Usage</strong></p>
<pre v-pre><code v-pre>v-tooltip=&quot;'tooltip content'&quot;
</code></pre>
<p><strong>Placements Examples</strong></p>
<pre v-pre><code v-pre>v-tooltip.left=&quot;'tooltip content'&quot;
v-tooltip.right=&quot;'tooltip content'&quot;
</code></pre>
<p><strong>Triggers Examples</strong></p>
<pre v-pre><code v-pre>v-tooltip.hover=&quot;'tooltip content'&quot;
v-tooltip.click=&quot;'tooltip content'&quot;
</code></pre>
<p><strong>Unenterable</strong></p>
<pre v-pre><code v-pre>v-tooltip.unenterable=&quot;'tooltip content'&quot;
</code></pre>
<p><strong>Custom append-to</strong></p>
<pre v-pre><code v-pre>v-tooltip:arg=&quot;'tooltip content'&quot;
</code></pre>
<p><code v-pre>arg</code> is the ID (without prefix <code v-pre>#</code>) of the element to append to, leave it empty to use default value <code v-pre>body</code>.</p>
<p><strong>Combination</strong></p>
<pre v-pre><code v-pre>v-tooltip.left.hover=&quot;'tooltip content'&quot;
v-tooltip:some-id.right.click=&quot;'tooltip content'&quot;
</code></pre>
</markdown-wrapper></template><script>
  
export default {components:{'tooltip-example':{"template":"<div class=\"markdown-live-example\"><btn type=\"primary\" id=\"btn\">Hover me!</btn>\n<tooltip text=\"Static tooltip content goes here\" target=\"#btn\"/>\n<!-- tooltip-example.vue --></div>"},'tooltip-directive':{"template":"<div class=\"markdown-live-example\"><btn v-tooltip=\"'Static tooltip content goes here'\" type=\"primary\">Hover me!</btn>\n<!-- tooltip-directive.vue --></div>"},'tooltip-placements':{"template":"<div class=\"markdown-live-example\"><btn v-tooltip.left=\"'Tooltip content on left'\" type=\"primary\">Left</btn>\n<btn v-tooltip.top=\"'Tooltip content on top'\" type=\"primary\">Top</btn>\n<btn v-tooltip.bottom=\"'Tooltip content on bottom'\" type=\"primary\">Bottom</btn>\n<btn v-tooltip.right=\"'Tooltip content on right'\" type=\"primary\">Right</btn>\n<!-- tooltip-placements.vue --></div>"},'tooltip-viewport':{"template":"<div class=\"markdown-live-example\"><div id=\"tooltip-viewport\" style=\"display: inline-block; padding: 20px; background-color: #eee\">\n  <btn v-tooltip=\"{text:'Tooltip content auto', viewport:'#tooltip-viewport'}\" type=\"primary\">Auto</btn>\n  <btn v-tooltip.left=\"{text:'Tooltip content on left', viewport:'#tooltip-viewport'}\" type=\"primary\">Left</btn>\n  <btn v-tooltip.top=\"{text:'Tooltip content on top', viewport:'#tooltip-viewport'}\" type=\"primary\">Top</btn>\n  <btn v-tooltip.bottom=\"{text:'Tooltip content on bottom', viewport:'#tooltip-viewport'}\" type=\"primary\">Bottom</btn>\n  <btn v-tooltip.right=\"{text:'Tooltip content on right', viewport:'#tooltip-viewport'}\" type=\"primary\">Right</btn>\n  <btn v-tooltip=\"{text:'Tooltip content auto', viewport:'#tooltip-viewport'}\" type=\"primary\">Auto</btn>\n</div>\n<!-- tooltip-viewport.vue --></div>"},'tooltip-triggers':{"template":"<div class=\"markdown-live-example\"><btn v-tooltip=\"'Static tooltip content'\" type=\"primary\">Hover-Focus (Default)</btn>\n<btn v-tooltip.hover=\"'Static tooltip content'\" type=\"primary\">Hover</btn>\n<btn v-tooltip.focus=\"'Static tooltip content'\" type=\"primary\">Focus</btn>\n<btn v-tooltip.click=\"'Static tooltip content'\" type=\"primary\">Click</btn>\n<btn v-tooltip.outside-click=\"'Static tooltip content'\" type=\"primary\">Outside-Click</btn>\n<!-- tooltip-triggers.vue --></div>"},'tooltip-manual-trigger':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <tooltip text=\"Static tooltip content goes here\" trigger=\"manual\" v-model=\"show\">\n      <btn>You Can't Trigger Tooltip Here...</btn>\n    </tooltip>\n    <hr/>\n    <btn type=\"primary\" @click=\"show = !show\">Toggle Tooltip</btn>\n  </section>\n</div>",
    data () {
      return {
        show: false
      }
    }
  },'tooltip-disable':{"template":"<div class=\"markdown-live-example\"><tooltip text=\"Static tooltip content goes here\" :enable=\"false\">\n  <btn type=\"primary\">Disabled Tooltip</btn>\n</tooltip>\n<!-- tooltip-disable.vue --></div>"}}}</script>